/**
 * The database's enum values, so a dropdown cannot offer something the schema will
 * refuse.
 *
 * These lists used to be typed into each screen by hand and drifted from the schema:
 * a "grocer" buyer_type that never existed, a "field" order channel that never
 * existed. Nothing catches that until a person fills in a form and the insert is
 * rejected. Now the schema answers the question.
 */
import { useApi } from './hooks'

export type EnumMap = Record<string, string[]>

/** "part_paid" -> "Part paid", "on_terms" -> "On terms". */
export function enumLabel(value: string) {
  const words = value.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * Options for one enum type, ready for a select. Pass `labels` to override the
 * wording for values where the bare name is not plain enough for the person reading
 * it — anything not named there falls back to the value itself, tidied up.
 */
export function useEnumOptions(
  typeName: string,
  labels: Record<string, string> = {},
): Array<{ value: string; label: string }> {
  const enums = useApi<EnumMap>('/reference/enums', { cacheKey: 'reference.enums' })
  return (enums.data?.[typeName] ?? []).map((v) => ({
    value: v,
    label: labels[v] ?? enumLabel(v),
  }))
}
